function projectQueryPredicate(projectId) {
    const pid = String(projectId ?? '');
    return (query) => {
        const key = query.queryKey;
        if (!Array.isArray(key) || key[0] !== 'projects') return false;
        return String(key[1] ?? '') === pid || String(key[2] ?? '') === pid;
    };
}

/**
 * Remove all project-related query caches except milestones and project detail.
 * Use after milestone update: only GET /milestones runs; project detail stays cached so shell does not refetch.
 * Other tab caches (sprints, qa, bugs, etc.) are cleared so those tabs fetch when opened.
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {string} projectId
 */
export function removeProjectQueriesExceptMilestones(queryClient, projectId) {
    if (!projectId) return;
    const pid = String(projectId ?? '');
    const predicate = (query) => {
        const key = query.queryKey;
        if (!Array.isArray(key) || key[0] !== 'projects') return false;
        const matchesProject = String(key[1] ?? '') === pid || String(key[2] ?? '') === pid;
        const isMilestones = String(key[1] ?? '') === pid && key[2] === 'milestones';
        const isProjectDetail = key[1] === 'detail' && String(key[2] ?? '') === pid;
        return matchesProject && !isMilestones && !isProjectDetail;
    };
    queryClient.removeQueries({ predicate });
}

/**
 * Invalidate and refetch all project-detail–related queries for a project.
 * Call this after any POST/PATCH/PUT/DELETE that changes project data
 * so that all tabs (including QA) see fresh data without a full page refresh.
 * refetchType: 'all' refetches active and inactive queries (e.g. QA tab not mounted).
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {string} projectId
 */
export function invalidateProjectQueries(queryClient, projectId) {
    if (!projectId) return;
    const predicate = projectQueryPredicate(projectId);
    queryClient.invalidateQueries({ predicate, refetchType: 'all' });
}

/**
 * Invalidate all project list/detail caches used by phase/milestone/sprint rails.
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {string} projectId
 */
export function invalidateProjectScopeLists(queryClient, projectId) {
    if (!projectId) return;
    invalidateProjectQueries(queryClient, projectId);
    queryClient.invalidateQueries({
        queryKey: ['projects', 'detail', projectId],
        refetchType: 'all',
    });
}

/**
 * Invalidate project queries but **not** the paginated issues-and-upgrades **list** cache
 * (`['projects', projectId, 'issues-and-upgrades', { page, limit }]`).
 * Use after merging linked tasks into that list client-side (GET list may omit `tasks[]`).
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {string} projectId
 */
export function invalidateProjectQueriesExceptIssueUpgradeList(queryClient, projectId) {
    if (!projectId) return;
    const pid = String(projectId ?? '');
    queryClient.invalidateQueries({
        predicate: (query) => {
            const key = query.queryKey;
            if (!Array.isArray(key) || key[0] !== 'projects') return false;
            const matchesProject = String(key[1] ?? '') === pid || String(key[2] ?? '') === pid;
            if (!matchesProject) return false;
            if (
                key.length === 4 &&
                String(key[1]) === pid &&
                key[2] === 'issues-and-upgrades' &&
                key[3] != null &&
                typeof key[3] === 'object' &&
                !Array.isArray(key[3])
            ) {
                return false;
            }
            return true;
        },
        refetchType: 'all',
    });
}

/**
 * Invalidate only task-related queries for a project.
 * Use after task creation/update to prevent refetching the full project, bugs, blockers, etc.
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {string} projectId
 */
export function invalidateProjectTaskQueries(queryClient, projectId) {
    if (!projectId) return;
    const pid = String(projectId ?? '');
    queryClient.invalidateQueries({
        predicate: (query) => {
            const key = query.queryKey;
            if (!Array.isArray(key) || key[0] !== 'projects' || String(key[1] ?? '') !== pid) return false;
            // Only invalidate project-wide tasks and sprint details (which contain sprint tasks)
            return key[2] === 'tasks' || key[2] === 'sprint-detail' || (key[2] === 'sprints' && key[4] === 'detail');
        },
        refetchType: 'all',
    });
}
